import * as React from 'react';
import { useState } from 'react';

import {
  Container,
  ExpandIcon,
  FAQAnswer,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQsSection,
  QuestionText,
  SectionHeading,
} from './faqs.styles';

interface FAQ {
  id: number;
  question: string;
  answer: React.ReactNode;
}

const faqData: FAQ[] = [
  {
    id: 1,
    question: 'What markets is Clutter currently active in?',
    answer: (
      <>
        Clutter currently serves residents across the following metro areas:
        <br />
        <strong>
          Los Angeles, Orange County, San Diego, San Francisco, Stockton,
          Seattle, Chicago, New York / New Jersey, Philadelphia, Boston,
          Washington D.C., Dallas, Toronto, Miami, Phoenix, Tampa Bay, Denver,
          Pittsburgh, and Houston.
        </strong>
      </>
    ),
  },
  {
    id: 2,
    question: 'Where are residents’ items stored?',
    answer: (
      <>
        All items are stored{' '}
        <strong>
          locally in secure, climate-controlled Iron Mountain warehouses
        </strong>
        , ensuring top-tier safety and environmental protection for residents'
        belongings. Each item is digitally cataloged in the Clutter app for easy
        viewing and retrieval.
      </>
    ),
  },
  {
    id: 3,
    question: 'Is there any cost to the partner?',
    answer: (
      <>
        Partnership cost depends on the selected model:
        <ul>
          <li>
            <strong>Referral Partners</strong> typically have no cost to
            participate.
          </li>
          <li>
            <strong>Reseller or integrated partners</strong> may purchase
            storage or moving services in bulk at preferred rates to offer
            directly to their residents or customers.
          </li>
        </ul>
        Our team will work with you to determine the best structure for your
        organization.
      </>
    ),
  },
  {
    id: 4,
    question: 'How is pricing structured for end users?',
    answer: (
      <>
        Pricing can be configured based on <strong>the partnership type</strong>{' '}
        and <strong>how the service is positioned to residents</strong>.
        <br />
        <br />
        Partners can choose to:
        <ul>
          <li>
            Pass along <strong>Clutter’s standard retail pricing</strong>, or
          </li>
          <li>
            Offer <strong>exclusive partner-only discounts</strong>, or
          </li>
          <li>
            <strong>Bundle storage/moving as an amenity</strong> within resident
            fees or employee benefits.
          </li>
        </ul>
        Our team provides guidance on the ideal pricing setup for your program.
      </>
    ),
  },
  {
    id: 5,
    question: 'What marketing support is provided?',
    answer: (
      <>
        Yes — Clutter has a{' '}
        <strong>dedicated marketing and partnerships team</strong> to help you
        stand up your program and drive adoption.
        <br />
        <br />
        Support includes:
        <ul>
          <li>Custom co-branded landing page</li>
          <li>Launch announcements and digital assets</li>
          <li>Ongoing promotional support for seasonal or renewal campaigns</li>
        </ul>
      </>
    ),
  },
  {
    id: 6,
    question: 'How do I get started?',
    answer: (
      <>
        Getting started is simple:
        <ol>
          <li>
            <strong>Submit your interest</strong> through the form below.
          </li>
          <li>
            <strong>Meet with our partnerships team</strong> to align on goals
            and setup.
          </li>
          <li>
            <strong>Launch your co-branded program</strong> with dedicated
            marketing and operational support from Clutter.
          </li>
        </ol>
        Partnerships can be stood up and get live in as little as a few weeks.
      </>
    ),
  },
];

export const FAQsComponent: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<number[]>([1]); // First item expanded by default

  const toggleExpanded = (id: number) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  return (
    <FAQsSection>
      <Container>
        <SectionHeading>FAQs</SectionHeading>
        <FAQContainer>
          {faqData.map((faq) => (
            <FAQItem key={faq.id} role="region">
              <FAQQuestion
                onClick={() => toggleExpanded(faq.id)}
                expanded={expandedItems.includes(faq.id)}
                aria-expanded={expandedItems.includes(faq.id)}
                aria-controls={`faq-answer-${faq.id}`}
              >
                <QuestionText>{faq.question}</QuestionText>
                <ExpandIcon expanded={expandedItems.includes(faq.id)}>
                  {expandedItems.includes(faq.id) ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 13V11H21V13H3Z" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M11 21V13H3V11H11V3H13V11H21V13H13V21H11Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </ExpandIcon>
              </FAQQuestion>
              {expandedItems.includes(faq.id) && (
                <FAQAnswer id={`faq-answer-${faq.id}`}>{faq.answer}</FAQAnswer>
              )}
            </FAQItem>
          ))}
        </FAQContainer>
      </Container>
    </FAQsSection>
  );
};
